import React from "react";
import {
    Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StoreIcon from "@mui/icons-material/Store";
import VisibilityIcon from "@mui/icons-material/Visibility";
import type { Transaction } from "../../../../hooks/Clients/dashboard";
import { CanalChip, TxDetails, DialogSection, dataCellSx, formatAmount, formatDate, headerCellSx } from "./ui";

const getAccCumul = (tx: Transaction) => {
    const results: { value: string; head: string }[] = [];
    let i = 0;
    while (tx[`ACCOUNTCUMUL${i}`] !== undefined) {
        results.push({ value: String(tx[`ACCOUNTCUMUL${i}`] ?? ""), head: String(tx[`ACCOUNTHEADCUMUL${i}`] ?? `Compte ${i}`) });
        i++;
    }
    return results;
};

const TxRow = ({ icon, label, value, mono = false, highlight = false }: { icon: React.ReactNode; label: string; value?: string | number | null; mono?: boolean; highlight?: boolean }) => {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
            <Box sx={{ color: "primary.main", opacity: 0.75, flexShrink: 0, display: "flex" }}>{icon}</Box>
            <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" fontWeight={highlight ? 700 : 500} color={highlight ? "primary.main" : "text.primary"} sx={mono ? { fontFamily: "monospace", fontSize: "0.82rem" } : {}} noWrap>
                {String(value)}
            </Typography>
        </Box>
    );
};

interface TransactionDetailDialogProps {
    selectedTx: Transaction | null;
    setSelectedTx: (tx: Transaction | null) => void;
    txDetails: TxDetails | null;
    loadingTxDetails: boolean;
    offersDialogOpen: boolean;
    setOffersDialogOpen: (open: boolean) => void;
}

const TransactionDetailDialog = ({ selectedTx, setSelectedTx, txDetails, loadingTxDetails, offersDialogOpen, setOffersDialogOpen }: TransactionDetailDialogProps) => (
    <>
        <Dialog open={!!selectedTx} onClose={() => setSelectedTx(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}>
            {selectedTx && (
                <>
                    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                        <ReceiptLongIcon fontSize="small" color="primary" />
                        <Typography fontWeight={700} sx={{ flex: 1 }}>Détail de la transaction</Typography>
                        {selectedTx.AMOUNT && (
                            <Chip label={formatAmount(selectedTx.AMOUNT as number | string | null)} size="small" color="primary" sx={{ fontWeight: 700, fontSize: "0.8rem" }} />
                        )}
                        <IconButton size="small" onClick={() => setSelectedTx(null)} sx={{ ml: 0.5, color: "text.secondary" }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ p: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                        {/* ── Sections fixes (Identifiants / Transaction / Soldes) ── */}
                        <Box sx={{ flexShrink: 0 }}>
                            <DialogSection label="Identifiants" />
                            <Box sx={{ px: 2.5, py: 0.5 }}>
                                <TxRow icon={<ReceiptLongIcon fontSize="small" />} label="N° Ticket" value={selectedTx.RECEIPTNUMBER} mono />
                                <TxRow icon={<CreditCardIcon fontSize="small" />} label="N° Carte" value={selectedTx.IDENTIFIER_PERSON} mono />
                            </Box>

                            <DialogSection label="Transaction" />
                            <Box sx={{ px: 2.5, py: 0.5 }}>
                                <TxRow icon={<StoreIcon fontSize="small" />} label="Magasin" value={selectedTx.STORE_NAME || selectedTx.STORE} />
                                <TxRow icon={<CalendarTodayIcon fontSize="small" />} label="Date achat" value={formatDate(selectedTx.TRANSACTIONDATE)} />
                                <TxRow icon={<CalendarTodayIcon fontSize="small" />} label="Date intégration" value={formatDate(selectedTx.VALIDATIONDATE)} />
                                <TxRow icon={<EventNoteIcon fontSize="small" />} label="Source" value={selectedTx.TRANSACTIONSOURCE} />
                                <TxRow icon={<EventNoteIcon fontSize="small" />} label="Événement" value={selectedTx.EVENEMENT} />
                                {Number(selectedTx.COUNTLINKOFFER) > 0 && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                                        <Box sx={{ color: "primary.main", opacity: 0.75, flexShrink: 0, display: "flex" }}><AccountBalanceWalletIcon fontSize="small" /></Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>Offres liées</Typography>
                                        <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ flex: 1 }}>{String(selectedTx.COUNTLINKOFFER)}</Typography>
                                        <Tooltip title="Voir les offres liées">
                                            <IconButton size="small" color="primary" onClick={() => setOffersDialogOpen(true)} sx={{ mr: 0.5 }}>
                                                {loadingTxDetails ? <CircularProgress size={16} color="inherit" /> : <VisibilityIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                                {selectedTx.DISCOUNT_AMOUNT != null && selectedTx.DISCOUNT_AMOUNT !== "" && (
                                    <TxRow icon={<PointOfSaleIcon fontSize="small" />} label="Remise" value={formatAmount(selectedTx.DISCOUNT_AMOUNT as string | number | null)} highlight />
                                )}
                            </Box>

                            {getAccCumul(selectedTx).length > 0 && (
                                <>
                                    <DialogSection icon={<AccountBalanceWalletIcon />} label="Soldes" />
                                    <Box sx={{ px: 2.5, py: 0.5 }}>
                                        {getAccCumul(selectedTx).map((acc, i) => (
                                            <TxRow
                                                key={i}
                                                icon={<AccountBalanceWalletIcon fontSize="small" />}
                                                label={acc.head}
                                                value={acc.value}
                                                highlight
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>

                        {/* ── Section Articles ── */}
                        {(loadingTxDetails || (txDetails?.transaction_items && txDetails.transaction_items.length > 0)) && (
                            <Box sx={{ flexShrink: 0 }}>
                                <DialogSection label="Articles du ticket" />
                                {loadingTxDetails ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
                                ) : (
                                    <TableContainer sx={{ overflowX: "hidden" }}>
                                        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                                            <colgroup>
                                                <col style={{ width: "42%" }} />
                                                <col style={{ width: "20%" }} />
                                                <col style={{ width: "8%" }} />
                                                <col style={{ width: "16%" }} />
                                                <col style={{ width: "14%" }} />
                                            </colgroup>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ ...headerCellSx, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>Libellé</TableCell>
                                                    <TableCell sx={{ ...headerCellSx, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>Réf.</TableCell>
                                                    <TableCell sx={{ ...headerCellSx, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>Qté</TableCell>
                                                    <TableCell sx={{ ...headerCellSx, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>Prix TTC</TableCell>
                                                    <TableCell sx={{ ...headerCellSx, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}>Remise</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {txDetails?.transaction_items?.map((item, i) => (
                                                    <TableRow key={item.REFERENCE || i} hover>
                                                        <TableCell sx={dataCellSx}>
                                                            <Tooltip title={item.LIBELLE || ""} placement="top" arrow disableHoverListener={!item.LIBELLE}>
                                                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.LIBELLE || "—"}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={dataCellSx}>
                                                            <Tooltip title={item.REFERENCE || ""} placement="top" arrow disableHoverListener={!item.REFERENCE}>
                                                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.REFERENCE || "—"}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={dataCellSx}>{item.QUANTITE || "—"}</TableCell>
                                                        <TableCell sx={dataCellSx}>{item.TTC || "—"}</TableCell>
                                                        <TableCell sx={dataCellSx}>{item.REMISE || "—"}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
                        <Button onClick={() => setSelectedTx(null)} variant="outlined" size="small">Fermer</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>

        <Dialog open={offersDialogOpen} onClose={() => setOffersDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <AccountBalanceWalletIcon fontSize="small" color="primary" />
                <Typography fontWeight={700} sx={{ flex: 1 }}>Offres liées — {selectedTx?.RECEIPTNUMBER || "transaction"}</Typography>
                <Chip label={`${selectedTx?.COUNTLINKOFFER ?? 0} offre(s)`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                <IconButton size="small" onClick={() => setOffersDialogOpen(false)} sx={{ ml: 0.5, color: "text.secondary" }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {loadingTxDetails ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
                ) : txDetails?.transaction_offers && txDetails.transaction_offers.length > 0 ? (
                    <TableContainer sx={{ overflowX: "hidden" }}>
                        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                            <colgroup>
                                <col style={{ width: "14%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "26%" }} />
                                <col style={{ width: "6%" }} />
                                <col style={{ width: "24%" }} />
                            </colgroup>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellSx}>N° opération</TableCell>
                                    <TableCell sx={headerCellSx}>Date envoi</TableCell>
                                    <TableCell sx={headerCellSx}>Libellé</TableCell>
                                    <TableCell sx={headerCellSx}>Offre</TableCell>
                                    <TableCell sx={{ ...headerCellSx, textAlign: "center" }} />{/* Canal — icône uniquement, pas de titre */}
                                    <TableCell sx={headerCellSx}>Validité</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {txDetails.transaction_offers.map((off, i) => (
                                    <TableRow key={off.NUMOPERATION || i} hover>
                                        <TableCell sx={dataCellSx}>
                                            <Tooltip title={off.NUMOPERATION || ""} placement="top" arrow disableHoverListener={!off.NUMOPERATION}>
                                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{off.NUMOPERATION || "—"}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={dataCellSx}>{off.SENTDATE || "—"}</TableCell>
                                        <TableCell sx={dataCellSx}>
                                            <Tooltip title={off.OPERATIONNAME || ""} placement="top" arrow disableHoverListener={!off.OPERATIONNAME}>
                                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{off.OPERATIONNAME || "—"}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={dataCellSx}>
                                            <Tooltip title={off.OFFERDESCRIPTION || ""} placement="top" arrow disableHoverListener={!off.OFFERDESCRIPTION}>
                                                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{off.OFFERDESCRIPTION || "—"}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ ...dataCellSx, overflow: "visible", px: 1, textAlign: "center" }}>
                                            {off.CANAL ? <CanalChip canal={off.CANAL} /> : "—"}
                                        </TableCell>
                                        <TableCell sx={dataCellSx}>
                                            {off.STARTDATE && off.ENDDATE ? `${off.STARTDATE} au ${off.ENDDATE}` : (off.STARTDATE || off.ENDDATE || "—")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                        <AccountBalanceWalletIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                        <Typography color="text.secondary">Aucune offre liée trouvée</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
                <Button onClick={() => setOffersDialogOpen(false)} variant="outlined" size="small">Fermer</Button>
            </DialogActions>
        </Dialog>
    </>
);

export default TransactionDetailDialog;
